import React, { useState } from "react";
import { Avatar, Box, IconButton } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useAuthenticationStore } from "../../../stores/auth";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { ProfilePictureDialog } from "./ProfilePictureDialog";
import { alpha } from "@mui/material/styles";

export const ProfilePicture = ({ editable = false, size = 56 }) => {
  const { user: authUser, updateUserInfo } = useAuthenticationStore();
  const user = authUser?.info;
  const apiClient = useApiClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const refreshUser = async () => {
    const userData = await apiClient.users.me();
    updateUserInfo(userData);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    try {
      const result = await apiClient.users.updateProfilePicture({
        image: file,
      });

      if (result.success) {
        await refreshUser();
        enqueueSnackbar("Profile picture updated successfully", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(result.message || "Failed to update profile picture", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      enqueueSnackbar("Error uploading profile picture. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const result = await apiClient.users.deleteProfilePicture();

      if (result.success) {
        await refreshUser();
        enqueueSnackbar("Profile picture deleted successfully", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(result.message || "Failed to delete profile picture", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      enqueueSnackbar("Error deleting profile picture. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <>
      <Box
        position="relative"
        display="inline-block"
        onMouseEnter={() => editable && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          cursor: editable ? "pointer" : "default",
        }}
        onClick={() => editable && setDialogOpen(true)}
      >
        <Avatar
          src={user?.profilePictureUrl || undefined}
          alt="User profile picture"
          aria-label="User profile picture"
          sx={{ width: size, height: size }}
        />
        {editable && isHovered && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: size,
              height: size,
              borderRadius: "50%",
              backgroundColor: (theme) =>
                alpha(theme.palette.common.black, 0.5),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.2s",
            }}
          >
            <IconButton
              size="small"
              sx={{
                color: "common.white",
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.common.white, 0.1),
                },
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
      </Box>

      {editable && (
        <ProfilePictureDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onUpload={handleUpload}
          onDelete={handleDelete}
          hasProfilePicture={!!user?.profilePictureUrl}
        />
      )}
    </>
  );
};
